import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';

export type AuthSessionEventType = 'login' | 'logout' | 'refresh';

export interface AuthSessionEvent {
  id: string;
  sourceId: string;
  type: AuthSessionEventType;
  fingerprint: string | null;
  timestamp: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthSessionCoordinatorService {
  private readonly channelName = 'autof-auth-session';
  private readonly storageKey = 'autof-auth-session-event';
  private readonly sourceId = this.createId();
  private readonly processedEventIds: string[] = [];
  private readonly eventsSubject = new Subject<AuthSessionEvent>();
  private readonly channel =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(this.channelName) : null;

  readonly events$ = this.eventsSubject.asObservable();

  constructor(private cookieService: CookieService) {
    this.channel?.addEventListener('message', (message) => {
      this.consumeEvent(message?.data);
    });

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (event) => {
        if (event.key !== this.storageKey || !event.newValue) {
          return;
        }

        try {
          this.consumeEvent(JSON.parse(event.newValue));
        } catch {
          return;
        }
      });
    }
  }

  publish(type: AuthSessionEventType) {
    const event: AuthSessionEvent = {
      id: this.createId(),
      sourceId: this.sourceId,
      type,
      fingerprint: this.getCurrentFingerprint(),
      timestamp: Date.now()
    };

    this.rememberEvent(event.id);
    this.channel?.postMessage(event);

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(event));
      } catch {
        return;
      }
    }
  }

  getCurrentFingerprint(): string | null {
    const token =
      this.cookieService.get('access_token') || this.cookieService.get('refresh_token') || '';

    if (!token) {
      return null;
    }

    const payload = this.decodeJwtPayload(token);
    const sub = Number(payload?.sub || 0) || 0;
    const sid = typeof payload?.sid === 'string' ? payload.sid : null;

    if (sub && sid) {
      return `${sub}:${sid}`;
    }

    if (sub) {
      return `${sub}:legacy`;
    }

    return `legacy:${token.slice(-12)}`;
  }

  private consumeEvent(value: unknown) {
    const event = this.normalizeEvent(value);

    if (!event || event.sourceId === this.sourceId || this.processedEventIds.includes(event.id)) {
      return;
    }

    this.rememberEvent(event.id);
    this.eventsSubject.next(event);
  }

  private normalizeEvent(value: any): AuthSessionEvent | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    if (!value.id || !value.sourceId || !value.type) {
      return null;
    }

    return {
      id: String(value.id),
      sourceId: String(value.sourceId),
      type: value.type as AuthSessionEventType,
      fingerprint: value.fingerprint ? String(value.fingerprint) : null,
      timestamp: Number(value.timestamp || Date.now())
    };
  }

  private rememberEvent(eventId: string) {
    this.processedEventIds.push(eventId);

    if (this.processedEventIds.length > 20) {
      this.processedEventIds.shift();
    }
  }

  private decodeJwtPayload(token: string): Record<string, any> | null {
    const parts = token.split('.');

    if (parts.length < 2 || !parts[1]) {
      return null;
    }

    try {
      const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
      return JSON.parse(globalThis.atob(`${normalized}${padding}`));
    } catch {
      return null;
    }
  }

  private createId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
